import { useEffect, useRef, useState } from 'react';
import { X, Send, Mail } from 'lucide-react';
import { renderWorkbookReadyEmail } from '@/lib/email-templates';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface EmailPreviewModalProps {
  recipientName: string;
  recipientEmail: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDelivering: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EmailPreviewModal({
  recipientName,
  recipientEmail,
  onConfirm,
  onCancel,
  isDelivering,
}: EmailPreviewModalProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(500);

  const displayName = recipientName || 'there';
  const emailHtml = renderWorkbookReadyEmail(displayName);

  // Write email HTML into the iframe and auto-size it
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const doc = iframe.contentDocument;
    if (!doc) return;

    doc.open();
    doc.write(emailHtml);
    doc.close();

    // Resize iframe to fit content after a short delay for rendering
    const timer = setTimeout(() => {
      const body = doc.body;
      if (body) {
        setIframeHeight(Math.min(body.scrollHeight + 20, 600));
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [emailHtml]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />

      {/* Modal */}
      <div
        className="relative mx-4 flex w-full max-w-2xl flex-col rounded-xl border border-border bg-white shadow-xl"
        style={{ maxHeight: '90vh' }}
      >
        {/* Close button */}
        <button
          onClick={onCancel}
          className="absolute right-3 top-3 z-10 rounded-md p-1 text-text-muted hover:bg-gray-100 hover:text-text-primary"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header */}
        <div className="shrink-0 border-b border-border px-6 pt-6 pb-4">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="h-5 w-5 text-brand" />
            <h2 className="text-lg font-semibold text-text-primary">Email Preview</h2>
          </div>

          {/* Recipient info */}
          <div className="rounded-lg bg-gray-50 px-4 py-3 text-sm">
            <div className="flex justify-between">
              <span className="text-text-muted">To</span>
              <span className="text-text-primary">
                {recipientName || 'Unknown'}{' '}
                <span className="text-text-muted">&lt;{recipientEmail}&gt;</span>
              </span>
            </div>
            <div className="mt-1 flex justify-between">
              <span className="text-text-muted">Subject</span>
              <span className="text-text-primary">Your Ellie workbook is ready to download!</span>
            </div>
          </div>
        </div>

        {/* Email preview (scrollable) */}
        <div className="min-h-0 flex-1 overflow-auto px-6 py-4">
          <div className="overflow-hidden rounded-lg border border-border">
            <iframe
              ref={iframeRef}
              title="Email preview"
              sandbox="allow-same-origin"
              style={{ width: '100%', height: iframeHeight, border: 'none' }}
            />
          </div>
          <p className="mt-2 text-center text-xs text-text-muted">
            The download link will be generated during delivery.
          </p>
        </div>

        {/* Actions */}
        <div className="shrink-0 border-t border-border px-6 py-4">
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              disabled={isDelivering}
              className="flex-1 rounded-lg border border-border px-4 py-2 text-sm font-medium text-text-primary transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isDelivering}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isDelivering ? (
                <>
                  <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Delivering…
                </>
              ) : (
                <>
                  <Send className="h-3.5 w-3.5" />
                  Confirm & Deliver
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
